import { useState, useEffect, useCallback } from 'react';
import { Modal } from 'antd';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import React from 'react';
import { billingDisabled, StripePK } from '../CcxEnv';
import useCards from './useCards';
import AddCard from '../../components/account/AddCard';
import { useAppDispatch } from '../../redux/hooks';
import { checkUserSubscription } from '../../slices/user.slice';

interface UseCreditCardRequiredOptions {
    title?: string;
    description?: string;
    modalWidth?: number;
    includePromoCode?: boolean;
}

interface UseCreditCardRequiredReturn {
    checkCreditCardAndExecute: (
        action: () => void | Promise<void>
    ) => Promise<void>;
    hasValidCards: boolean;
    loading: boolean;
    showModal: (onSuccess?: () => void) => void;
    hideModal: () => void;
    isModalVisible: boolean;
    modal: React.ReactElement | null;
}

/**
 * Hook that provides functionality to check for credit cards before executing actions.
 * Returns methods to check cards and show the AddCard modal with integrated promo code functionality.
 */
export default function useCreditCardRequired(
    options: UseCreditCardRequiredOptions = {}
): UseCreditCardRequiredReturn {
    const {
        title = 'Credit Card Required',
        description = 'A valid credit card is required for this action. Please add one to continue.',
        modalWidth = 800,
        includePromoCode = true,
    } = options;

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [pendingAction, setPendingAction] = useState<
        (() => void | Promise<void>) | null
    >(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [previousHasValidCards, setPreviousHasValidCards] = useState(false);

    const { cards, refresh, loading: cardsLoading } = useCards();
    const stripePromise = !billingDisabled ? loadStripe(StripePK) : null;
    const dispatch = useAppDispatch();

    const hasValidCards = Boolean(
        cards && cards.length > 0 && cards.some((card) => card.isValid())
    );
    const loading = cardsLoading || actionLoading;

    const executeAction = useCallback(
        async (action: () => void | Promise<void>) => {
            setActionLoading(true);
            try {
                await action();
            } finally {
                setActionLoading(false);
            }
        },
        []
    );

    // Track when valid cards state changes from false to true (successful card addition)
    useEffect(() => {
        if (!previousHasValidCards && hasValidCards && isModalVisible) {
            // Successfully added a valid card, close modal and execute action
            setIsModalVisible(false);

            if (pendingAction) {
                executeAction(pendingAction);
                setPendingAction(null);
            }
        }
    }, [
        hasValidCards,
        previousHasValidCards,
        isModalVisible,
        pendingAction,
        executeAction,
    ]);

    const checkCreditCardAndExecute = async (
        action: () => void | Promise<void>
    ) => {
        if (billingDisabled) {
            // If billing is disabled, proceed with action
            await executeAction(action);
            return;
        }

        if (hasValidCards) {
            // User has valid cards, proceed with action
            await executeAction(action);
        } else {
            // Store the action to execute after card is added
            setPendingAction(() => action);
            setPreviousHasValidCards(hasValidCards);
            setIsModalVisible(true);
        }
    };

    const showModal = (onSuccess?: () => void) => {
        if (onSuccess) {
            setPendingAction(() => onSuccess);
        }
        setPreviousHasValidCards(hasValidCards);
        setIsModalVisible(true);
    };

    const hideModal = () => {
        setIsModalVisible(false);
        setPendingAction(null);
        setPreviousHasValidCards(false);
    };

    const handleCardAdded = async () => {
        // Refresh cards list - the useEffect will handle success detection
        await refresh();
        // Refresh the subscription so downstream steps (e.g. the datastore
        // wizard's billing step) see the now-valid subscription right away,
        // instead of relying on the 60s auto-refresh in AppProviders.
        dispatch(checkUserSubscription());
    };

    const handleCancel = () => {
        hideModal();
    };

    // Create the modal element
    const modal = isModalVisible ? (
        <Modal
            title={title}
            open={isModalVisible}
            onCancel={handleCancel}
            width={modalWidth}
            footer={null}
            destroyOnClose
        >
            <div style={{ marginBottom: 16 }}>{description}</div>
            {stripePromise && (
                <Elements stripe={stripePromise}>
                    <AddCard
                        onSuccess={handleCardAdded}
                        submitText="Add Card & Continue"
                        includePromoCode={includePromoCode}
                        testId="CreditCardRequiredHookAddCard"
                    />
                </Elements>
            )}
        </Modal>
    ) : null;

    return {
        checkCreditCardAndExecute,
        hasValidCards,
        loading,
        showModal,
        hideModal,
        isModalVisible,
        modal,
    };
}
